import { useEffect, useRef, useState, Fragment } from "react";
import { gsap } from "animations/gsap";
import { useGradeManagementAnimations, useNormalizeScroll } from "animations";
import {
  Banner,
  Hero,
  ImageNavigation,
  IntroBox,
  LottieWrapper,
  Quote,
  ROM,
  WGYB,
} from "components";
import { desktopLottie } from "utils/isDesktop";
import { LottieInt, RefInt } from "interfaces";
import {
  compactTrackLoaderGM,
  crawlerDozerGM,
  excavatorGM,
  motorGraderGM,
  quoteBgGM,
  quotePersonGM,
  quoteTextureGM,
} from "assets/images";
import styles from "./GradeManagement.module.scss";
import boxStyles from "components/IntroBox/IntroBox.module.scss";

const GradeManagement = () => {
  const ref: RefInt = useRef(null),
    selectorRef: RefInt = useRef(null!);

  const [navLoc, setNavLoc] = useState<any>(0),
    [lottieTitle, setLottieTitle] = useState<any>(0),
    [upperOffset, setUpperOffset] = useState<number>(0),
    [offsets, _setOffsets] = useState<any>([]);

  useNormalizeScroll();
  useGradeManagementAnimations(ref, setUpperOffset);

  const exportOffsets = (response: any) => {
    _setOffsets(response.offsets);
  };

  const transitionDur = 250;

  useEffect(() => {
    const handleScroll = () => {
      let scrolled = document.scrollingElement?.scrollTop || 0;
      if (scrolled <= offsets[1] + upperOffset - transitionDur / 2) {
        setNavLoc(0);
      } else if (scrolled <= offsets[2] + upperOffset - transitionDur / 2) {
        setNavLoc(1);
      } else if (scrolled <= offsets[3] + upperOffset - transitionDur / 2) {
        setNavLoc(2);
      } else {
        setNavLoc(3);
      }

      if (scrolled <= offsets[1] + upperOffset - 4 * transitionDur) {
        setLottieTitle(0);
      } else if (scrolled <= offsets[2] + upperOffset - 4 * transitionDur) {
        setLottieTitle(1);
      } else if (scrolled <= offsets[3] + upperOffset - 4 * transitionDur) {
        setLottieTitle(2);
      } else {
        setLottieTitle(3);
      }
    };
    if (offsets.length) {
      document.addEventListener("scroll", (e) => {
        e.preventDefault();
        handleScroll();
      });
    }
    return () => window.removeEventListener("scroll", handleScroll);
  }, [offsets, upperOffset]);

  useEffect(() => {
    switch (navLoc) {
      case 0:
        scrollNav(0);
        break;
      case 1:
        scrollNav(1);
        break;
      case 2:
        scrollNav(2);
        break;
      case 3:
        scrollNav(3);
        break;
      default:
        scrollNav(0);
    }
  }, [navLoc]);

  const windowWidth = window.innerWidth / 2,
    scrollNav = (index: number) => {
      gsap.to(selectorRef.current, {
        duration: 5,
        scrollTo: {
          x: `#nav-item-${index}`,
          offsetX: windowWidth / 2,
          autoKill: false,
        },
        ease: "none",
      });
    },
    scrollEnd = () => {
      setTimeout(() => {
        gsap.globalTimeline.timeScale(1);
        gsap.set(".anim-transition", { zIndex: 0 });
      }, 750);
    },
    scrollTo = (index: number) => {
      setNavLoc(index);
      gsap.set(".anim-transition", { zIndex: 9 });
      gsap.globalTimeline.timeScale(10000);
      gsap.set(window, {
        onComplete: () => scrollEnd(),
        scrollTo: {
          y: Math.ceil(offsets[index]) + upperOffset + 1,
          autoKill: false,
        },
        ease: "none",
      });
    },
    onClick = (index: number) => (_: any) => {
      scrollTo(index);
    },
    keyUp = (index: number) => (e: any) => {
      e.key === "Enter" && scrollTo(index);
    };
  const lotties: Array<LottieInt> = [
      {
        target: "grade-management-3dsg-lottie",
        lottie: desktopLottie
          ? "grade-management-3DSG_desktop"
          : "grade-management-3DSG_mobile",
        headlines: [
          <>
            Dig smarter.
            <br /> Get to grade faster.
          </>,
        ],
        titles: [
          <>Satellite positioning determines precise bucket position.</>,
          <>3D site plans are uploaded to equipment.</>,
          <>
            SmartGrade<sub>TM</sub> system automatically controls the boom and
            bucket.
          </>,
          <>Operator achieves precise grade through machine control.</>,
        ],
      },
      {
        target: "grade-management-3dgg-lottie",
        lottie: desktopLottie
          ? "grade-management-3DGG_desktop"
          : "grade-management-3DGG_mobile",
        titles: [
          <>Satellite positioning determines precise bucket position.</>,
          <>3D site plans are uploaded to equipment.</>,
          <>Operator digs manually, guided by 3D site plan information.</>,
          <>Operator is guided to reach and maintain precise 3D grade.</>,
        ],
      },
      {
        target: "grade-management-2dgc-lottie",
        lottie: desktopLottie
          ? "grade-management-2DGC_desktop"
          : "grade-management-2DGC_mobile",
        titles: [
          <>Laser communicates elevation.</>,
          <>Operator inputs target depth.</>,
          <>Machine automatically controls boom and bucket.</>,
          <>Overdig protect prevents digging deeper than grade.</>,
          <>Operator is assisted to reach and maintain a precise grade.</>,
        ],
      },
      {
        target: "grade-management-2dgg-lottie",
        lottie: desktopLottie
          ? "grade-management-2DGG_desktop"
          : "grade-management-2DGG_mobile",
        titles: [
          <>Laser communicates elevation.</>,
          <>Operator inputs target depth.</>,
          <>Operator digs manually, with laser reference.</>,
          <>Operator is guided to reach and maintain a precise grade.</>,
        ],
      },
    ],
    selectorTitles = [
      {
        dim: "3D",
        name: "SmartGrade",
        type: (
          <>
            SmartGrade<sub>TM</sub>
          </>
        ),
      },
      {
        dim: "3D",
        name: "Grade Guidance",
        type: <>Grade Guidance</>,
      },
      {
        dim: "2D",
        name: "Grade Control",
        type: <>Grade Control</>,
      },
      {
        dim: "2D",
        name: "Grade Guidance",
        type: <>Grade Guidance</>,
      },
    ],
    disabled = !offsets.length,
    selector = (
      <>
        <div
          ref={selectorRef}
          className={`${styles["selector-wrapper"]} anim-selector`}
        >
          <div
            className={`${styles.selector}`}
            aria-label="navigation menu to select 1 of 4 animations"
          >
            {Array(4)
              .fill(true)
              .map((_, i) => (
                <Fragment key={i}>
                  <button
                    id={`nav-item-${i}`}
                    className={`${
                      navLoc === i ? styles.active : ""
                    }                    `}
                    onMouseUp={onClick(i)}
                    onKeyUp={keyUp(i)}
                    disabled={disabled}
                    aria-labelledby={`nav-item-${i}`}
                    aria-selected={navLoc === i ? true : false}
                    tabIndex={navLoc !== i ? -1 : undefined}
                  >
                    <h4 className={styles["selector-dim"]}>
                      {selectorTitles[i].dim}
                    </h4>
                    <h4 className={styles["selector-type"]}>
                      {selectorTitles[i].type}
                    </h4>
                    <div className={styles.hover} />
                  </button>
                  {i !== 3 && <div className={styles.divider} />}
                </Fragment>
              ))}
          </div>
        </div>
        <div className={`${styles["transition-wrapper"]} anim-transition`}>
          <div className={styles.transition}>
            <h1 className="xl">{selectorTitles[lottieTitle].dim}</h1>
            <h4 className="xl">{selectorTitles[lottieTitle].type}</h4>
          </div>
        </div>
      </>
    );
  return (
    <div ref={ref} id={styles["grade-management"]}>
      <IntroBox
        primary={
          <div className={boxStyles.primary}>
            <div className={`${boxStyles.placeholder} anim-placeholder`}>
              <div className={`${boxStyles.numbers}`}></div>
              <h4 className="bold ticker">%</h4>
            </div>
            <h4 className={`${boxStyles.right} bold ticker anim-primary`}>
              OF WORK IS REWORK.
            </h4>
          </div>
        }
        secondary="UNACCEPTABLE."
        sub="MEDITERRANEAN JOURNAL OF SOCIAL SCIENCES"
        target={[3, 0]}
        unit="%"
      />
      <Hero
        title="GRADE MANAGEMENT SOLUTIONS"
        content="No one wants to do the same task twice. So to help you steer clear of multiple passes and over grading, we engineered flexible grade management tools to get you to grade faster – no matter where you are in your tech journey. Helping you get into smarter tools where it makes sense so you can start boosting your productivity – and your bottom line."
        bg="grade-management"
      />
      <Banner
        title="EXPLORE WAYS TO GET TO GRADE FASTER BELOW,"
        subtitle={
          <>
            OR{" "}
            <a
              href="https://www.deere.com/assets/pdfs/common/industries/construction/grade-management-solutions-dkagrade.pdf"
              target="_blank"
              rel="noreferrer"
              aria-label="download-our-brochure"
            >
              DOWNLOAD OUR BROCHURE
            </a>{" "}
            FOR MORE INFORMATION.
          </>
        }
      />
      <LottieWrapper
        lotties={lotties}
        selector={selector}
        exportOffsets={exportOffsets}
        upperOffset={upperOffset}
      />

      <Quote
        className="grade-management"
        quote="It's a big machine, but we can still get some precise movement and get that custom look and feel that we're looking for."
        name="Jason Baker"
        position="Owner, DREAMTRAXX"
        img={{ src: quotePersonGM, width: 621, height: 413 }}
        bg={quoteBgGM}
        texture={{ src: quoteTextureGM, width: 1364, height: 807 }}
        dark
      />
      <ROM
        header="the widest range of machines equipped with grade management tools"
        content={[
          {
            label: "Excavators",
            src: excavatorGM,
            width: 260,
            height: 180,
            href: "https://www.deere.com/en/excavators/",
          },
          {
            label: "Crawler Dozers",
            src: crawlerDozerGM,
            width: 260,
            height: 180,
            href: "https://www.deere.com/en/dozers/",
          },
          {
            label: "Motor Graders",
            src: motorGraderGM,
            width: 260,
            height: 180,
            href: "https://www.deere.com/en/motor-graders/",
          },
          {
            label: "Compact Track Loaders",
            src: compactTrackLoaderGM,
            width: 260,
            height: 180,
            href: "https://www.deere.com/en/loaders/compact-track-loaders/",
          },
        ]}
      />

      <ImageNavigation />
      <WGYB />
    </div>
  );
};

export default GradeManagement;
